import { faArrowLeft, faBox, faPrint } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { Button } from '@shared/components/ui/button';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Separator } from '@shared/components/ui/shadcn/separator';
import { useToast } from '@shared/hooks/use-toast';
import { usePermissions } from '@shared/hooks/usePermissions';
import { apiClientWH } from '@shared/services/ApiClient';
import { generateReusedLabel } from '@shared/utils/reusedLabelGenerator';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useReception } from '../../hooks/useReception';
import { useReceptionDestinationRefs } from '../../hooks/useReceptionDestinationRefs';
import {
  Package,
  canDeleteReception,
  canEditReception,
  canSplitPackage,
  getDestinationUrl,
  getPackageDestinationReference,
} from '../../models/Reception';
import { ReceiveApiAdapter } from '@modules/Reused/Receive/adapters/ReceiveApiAdapter';
import { PackageStatusBadge } from '../display/PackageStatusBadge';
import { ReceptionStatusBadge } from '../display/ReceptionStatusBadge';
import { ErrorState } from '../feedback/ErrorState';
import { LoadingState } from '../feedback/LoadingState';
import DeleteReceptionModal from '../modals/DeleteReceptionModal';
import EditReceptionModal from '../modals/EditReceptionModal';
import IdentifyPackagesModal from '../modals/IdentifyPackagesModal';
import SplitPackageModal from '../modals/SplitPackageModal';

/**
 * Página de detalle de una recepción individual
 * Diseño clean y minimalista
 */
export const ReceptionDetail = () => {
  const { t } = useTranslation('receptionsModule');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const receptionId = id ? parseInt(id) : null;

  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const reusedReceiveAdapter = useMemo(() => new ReceiveApiAdapter(httpClient), [httpClient]);

  // Permisos
  const canEditPermission = hasPermission('receptions.edit');
  const canDeletePermission = hasPermission('receptions.delete');

  // Refs para modales
  const editModalRef = useRef<ModalCenterRef>(null);
  const deleteModalRef = useRef<ModalCenterRef>(null);
  const identifyModalRef = useRef<ModalCenterRef>(null);
  const splitModalRef = useRef<ModalCenterRef>(null);

  // Estado para paquete seleccionado (para split)
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);

  // Cargar datos de la recepción
  const { reception, isLoading, error, refetch } = useReception(receptionId);

  const { refByPackageId } = useReceptionDestinationRefs(reception);
  const breadcrumbs = useMemo(
    () => [
      { label: t('title'), path: '/receptions' },
      { label: reception?.code || '...', path: `/receptions/${id}` },
    ],
    [t, reception?.code, id],
  );

  // Acciones del header
  const headerActions = useMemo(() => {
    if (!reception) return [];

    const actions = [];

    // Editar: requiere permiso Y estado editable
    if (canEditPermission && canEditReception(reception.status)) {
      actions.push({
        id: 'edit',
        label: t('button.edit'),
        onClick: () => editModalRef.current?.openModal(),
        variant: 'outline' as const,
      });
    }

    // Eliminar: requiere permiso Y condiciones de negocio
    if (canDeletePermission && canDeleteReception(reception)) {
      actions.push({
        id: 'delete',
        label: t('button.delete'),
        onClick: () => deleteModalRef.current?.openModal(),
        variant: 'outline' as const,
      });
    }

    return actions;
  }, [reception, t, canEditPermission, canDeletePermission]);

  usePageNavigation({
    breadcrumbs,
    headerActions,
  });

  const handleBack = useCallback(() => {
    navigate('/receptions');
  }, [navigate]);

  const handleOpenIdentify = useCallback(() => {
    identifyModalRef.current?.openModal();
  }, []);

  const handleSplitPackage = useCallback((pkg: Package) => {
    setSelectedPackage(pkg);
    setTimeout(() => {
      splitModalRef.current?.openModal();
    }, 50);
  }, []);

  const handlePrintRejectedPdf = useCallback(
    async (pkg: Package) => {
      if (!pkg.destination_id) return;

      try {
        const response = await reusedReceiveAdapter.getReception(pkg.destination_id);
        const returnedReception = response.data;

        const labelUrl = await generateReusedLabel({
          code: returnedReception.code,
          date: new Date(returnedReception.created_at.replace(' ', 'T')).toLocaleDateString('es-ES'),
        });

        window.open(labelUrl, '_blank');
      } catch (error: any) {
        console.error('Error al generar PDF de rehusados desde recepción:', error);
        toast({
          title: t('toasts.error.title', 'Error en la operación'),
          description:
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            t('toasts.error.unexpected', 'Error inesperado. Inténtalo de nuevo.'),
          variant: 'destructive',
        });
      }
    },
    [reusedReceiveAdapter, t, toast],
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPackageTypeLabel = (pkg: Package) => {
    if (pkg.package_type === 'OTHER' && pkg.package_type_other) {
      return pkg.package_type_other;
    }
    return t(`package_types.${pkg.package_type.toLowerCase()}`);
  };

  if (isLoading) {
    return (
      <div className="py-4 px-4 md:px-6">
        <LoadingState />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 px-4 md:px-6">
        <ErrorState error={error as Error} onRetry={refetch} />
      </div>
    );
  }

  if (!reception) {
    return (
      <div className="py-4 px-4 md:px-6">
        <div className="text-center py-12">
          <p className="text-subtext">{t('errors.reception_not_found')}</p>
          <Button variant="outline" onClick={handleBack} className="mt-4">
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2 !h-4 !w-4" />
            {t('button.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  const hasPendingPackages = reception.pending_qty > 0;
  const canIdentify = reception.status !== 'CLOSED' && hasPendingPackages;

  return (
    <div className="py-4 px-4 md:px-6">
      <div className="flex flex-col md:flex-row justify-between items-start gap-4 md:gap-6">
        {/* Columna Izquierda - Paquetes */}
        <div className="w-full md:w-[70%] flex flex-col gap-4 md:gap-5">
          {/* Lista de paquetes */}
          <SectionBox className="bg-card-bg">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">{t('modal.view.packages_list')}</h2>
              {canIdentify && (
                <Button variant="primary" size="sm" onClick={handleOpenIdentify}>
                  {t('button.identify')} ({reception.pending_qty})
                </Button>
              )}
            </div>

            {/* Encabezados - Solo desktop */}
            <div className="hidden md:grid grid-cols-12 gap-4 text-xs font-medium text-complementary-text border-b border-card-border pb-2 pt-4">
              <div className="col-span-4">{t('modal.create.package_type_label')}</div>
              <div className="col-span-2 text-center">{t('modal.view.current_quantity')}</div>
              <div className="col-span-2 text-center">{t('table.status')}</div>
              <div className="col-span-2">{t('modal.view.destination')}</div>
              <div className="col-span-2"></div>
            </div>

            <div className="space-y-0">
              {reception.packages && reception.packages.length > 0 ? (
                reception.packages
                  .filter((pkg) => pkg && pkg.id)
                  .map((pkg) => {
                    const destinationRef = refByPackageId.get(pkg.id) ?? getPackageDestinationReference(pkg);
                    return (
                      <div key={pkg.id} className="border-b border-card-border py-4 !mt-0">
                        {/* Desktop Layout */}
                        <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                          {/* Tipo de paquete */}
                          <div className="col-span-4 flex items-center gap-3">
                            <div className="w-8 h-8 flex-shrink-0 rounded-md bg-hover-press flex items-center justify-center">
                              <FontAwesomeIcon icon={faBox} className="text-icons !w-3 !h-3" />
                            </div>
                            <p className="text-sm font-medium">{getPackageTypeLabel(pkg)}</p>
                          </div>

                          {/* Cantidad */}
                          <div className="col-span-2 text-center">
                            <span className="text-sm font-medium">{pkg.quantity}</span>
                          </div>

                          {/* Estado */}
                          <div className="col-span-2 text-center">
                            <PackageStatusBadge status={pkg.status} />
                          </div>

                          {/* Destino y acciones de rehusados - Destination and rejected actions */}
                          <div className="col-span-2 flex items-center gap-2 flex-wrap min-w-0">
                            {pkg.destination_type ? (
                              (() => {
                                const destinationUrl = getDestinationUrl(pkg.destination_type, pkg.destination_id);
                                const badgeLabel =
                                  destinationRef?.trim() ||
                                  t(`destination_types.${pkg.destination_type.toLowerCase()}`);
                                return destinationUrl ? (
                                  <Link
                                    to={destinationUrl}
                                    title={badgeLabel}
                                    className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-primary text-white dark:bg-primary dark:text-white hover:bg-primary/90 dark:hover:bg-primary/90 h-7 px-2 max-w-full min-w-0 truncate"
                                  >
                                    {badgeLabel}
                                  </Link>
                                ) : (
                                  <span className="text-xs text-primary truncate max-w-full" title={badgeLabel}>
                                    {badgeLabel}
                                  </span>
                                );
                              })()
                            ) : (
                              <span className="text-xs text-complementary-text">—</span>
                            )}

                            {pkg.destination_type === 'REJECTED' && pkg.destination_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePrintRejectedPdf(pkg)}
                                data-testid={`print-rejected-pdf-button-${pkg.id}`}
                                className="text-xs"
                              >
                                <FontAwesomeIcon icon={faPrint} className="!h-3 !w-3 text-icons" />
                              </Button>
                            )}
                          </div>

                          {/* Acciones - Solo dividir */}
                          <div className="col-span-2 flex justify-end">
                            {canSplitPackage(pkg) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSplitPackage(pkg)}
                                className="text-xs"
                              >
                                {t('button.split')}
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Mobile Layout */}
                        <div className="md:hidden space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                              <div className="w-8 h-8 flex-shrink-0 rounded-md bg-hover-press flex items-center justify-center">
                                <FontAwesomeIcon icon={faBox} className="text-icons !w-3 !h-3" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate">{getPackageTypeLabel(pkg)}</p>
                                <p className="text-xs text-complementary-text">{pkg.quantity} uds</p>
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <PackageStatusBadge status={pkg.status} />
                            </div>
                          </div>

                          {pkg.destination_type && (
                            <div className="flex flex-col gap-1 pl-10 sm:pl-11 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {(() => {
                                  const destinationUrl = getDestinationUrl(pkg.destination_type, pkg.destination_id);
                                  const badgeLabel =
                                    destinationRef?.trim() ||
                                    t(`destination_types.${pkg.destination_type.toLowerCase()}`);
                                  return destinationUrl ? (
                                    <Link
                                      to={destinationUrl}
                                      title={badgeLabel}
                                      className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-primary text-white dark:bg-primary dark:text-white hover:bg-primary/90 dark:hover:bg-primary/90 h-7 px-2 max-w-full min-w-0 truncate"
                                    >
                                      {badgeLabel}
                                    </Link>
                                  ) : (
                                    <span className="text-primary text-xs truncate max-w-full" title={badgeLabel}>
                                      {badgeLabel}
                                    </span>
                                  );
                                })()}

                                {pkg.destination_type === 'REJECTED' && pkg.destination_id && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePrintRejectedPdf(pkg)}
                                    data-testid={`print-rejected-pdf-button-mobile-${pkg.id}`}
                                    className="text-xs"
                                  >
                                    <FontAwesomeIcon icon={faPrint} className="!h-3 !w-3 text-icons" />
                                    <span className="ml-1">{t('modal.view.print_rejected_pdf')}</span>
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}

                          {canSplitPackage(pkg) && (
                            <div className="pl-10 sm:pl-11">
                              <Button variant="outline" size="sm" onClick={() => handleSplitPackage(pkg)}>
                                {t('button.split')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <div className="text-center py-8 text-complementary-text">{t('modal.view.no_packages')}</div>
              )}
            </div>
          </SectionBox>
        </div>

        {/* Columna Derecha - Información */}
        <div className="w-full md:w-[30%] flex flex-col gap-4 md:gap-5">
          {/* Resumen */}
          <SectionBox className="bg-card-bg">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">{reception.code}</h2>
              <ReceptionStatusBadge status={reception.status} />
            </div>

            <div className="grid grid-cols-3 gap-2 md:gap-4 pt-4">
              <div className="text-center">
                <p className="text-xl md:text-2xl font-bold">{reception.total_qty}</p>
                <p className="text-xs text-complementary-text">Total</p>
              </div>
              <div className="text-center">
                <p className="text-xl md:text-2xl font-bold text-warning">{reception.pending_qty}</p>
                <p className="text-xs text-complementary-text">{t('table.pending_qty')}</p>
              </div>
              <div className="text-center">
                <p className="text-xl md:text-2xl font-bold text-success">{reception.identified_qty}</p>
                <p className="text-xs text-complementary-text">{t('table.identified_qty')}</p>
              </div>
            </div>

            {canIdentify && (
              <div className="pt-4 mt-4 border-t border-card-border">
                <Button variant="primary" className="w-full" onClick={handleOpenIdentify}>
                  {t('button.identify')} ({reception.pending_qty} pendientes)
                </Button>
              </div>
            )}
          </SectionBox>

          {/* Información del transporte */}
          <SectionBox className="bg-card-bg">
            <h2 className="text-lg font-bold">{t('modal.view.reception_info')}</h2>

            <div className="space-y-3 pt-4">
              <div className="flex justify-between">
                <span className="text-sm text-complementary-text">{t('table.carrier')}</span>
                <span className="text-sm font-medium">{reception.carrier}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-complementary-text">{t('table.vehicle_plate')}</span>
                <span className="text-sm font-medium">{reception.vehicle_plate}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-complementary-text">{t('table.driver_name')}</span>
                <span className="text-sm font-medium">{reception.driver_name}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-complementary-text">{t('table.created_at')}</span>
                <span className="text-sm font-medium">{formatDate(reception.created_at)}</span>
              </div>
              {reception.closed_at && (
                <>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-sm text-complementary-text">{t('table.closed_at')}</span>
                    <span className="text-sm font-medium text-success">{formatDate(reception.closed_at)}</span>
                  </div>
                </>
              )}
            </div>
          </SectionBox>
        </div>
      </div>

      {/* Modales */}
      <EditReceptionModal ref={editModalRef} receptionId={reception.id} />
      <DeleteReceptionModal ref={deleteModalRef} reception={reception} />
      <IdentifyPackagesModal ref={identifyModalRef} reception={reception} onComplete={refetch} />
      {selectedPackage && <SplitPackageModal ref={splitModalRef} reception={reception} pkg={selectedPackage} />}
    </div>
  );
};

export default ReceptionDetail;
